/**
 * Enhanced News Hooks
 * Provides optimized data fetching with intelligent error handling and fallback strategies
 */

import { useQuery, useInfiniteQuery, UseQueryOptions, UseInfiniteQueryOptions } from '@tanstack/react-query';
import { News, PaginatedResponse } from '@/types';
import NewsService from '@/services/NewsService';
import { queryKeys, STALE_TIME, GC_TIME } from '@/lib/queryClient';

// Types for hook parameters
export interface UseNewsOptions {
  enabled?: boolean;
  staleTime?: number;
  gcTime?: number;
  refetchOnWindowFocus?: boolean;
  retry?: boolean | number | ((failureCount: number, error: unknown) => boolean);
  select?: (data: any) => any;
}

export interface UseNewsListOptions extends UseNewsOptions {
  page?: number;
  perPage?: number;
  locationSlug?: string;
  sortBy?: 'latest' | 'popular' | 'views' | 'comments' | 'trending';
}

export interface UseNewsCategoryOptions extends UseNewsListOptions {
  categorySlug: string;
}

export interface UseNewsSearchOptions extends UseNewsListOptions {
  searchTerm: string;
}

// Enhanced error handling for news queries
const enhancedRetry = (failureCount: number, error: unknown) => {
  // Don't retry more than 3 times
  if (failureCount >= 3) return false;

  // Handle specific error types
  if (error instanceof Error) {
    const message = error.message.toLowerCase();

    // Don't retry for 4xx errors (client errors)
    if (message.includes('404') || message.includes('unauthorized') || message.includes('forbidden')) {
      return false;
    }

    // Retry fewer times for 503 errors (service unavailable)
    if (message.includes('503') && failureCount >= 2) {
      return false;
    }
  }

  return true;
};

// Default options for news queries
const defaultNewsOptions: UseNewsOptions = {
  enabled: true,
  staleTime: STALE_TIME.NEWS,
  gcTime: GC_TIME.NEWS,
  refetchOnWindowFocus: false,
  retry: enhancedRetry,
};

/**
 * Hook for fetching latest news
 */
export const useLatestNews = (options: UseNewsListOptions = {}) => {
  const { page = 1, perPage = 20, locationSlug, ...queryOptions } = options;

  return useQuery({
    queryKey: queryKeys.news.latest(locationSlug, perPage),
    queryFn: async () => {
      try {
        const response = await NewsService.fetchLatestNews(page, perPage, locationSlug);
        return response.data || [];
      } catch (error) {
        console.error('Latest news fetch error:', error);
        // Return empty array instead of throwing to prevent UI crashes
        return [];
      }
    },
    ...defaultNewsOptions,
    ...queryOptions,
  });
};

/**
 * Hook for fetching popular news
 */
export const usePopularNews = (options: UseNewsListOptions = {}) => {
  const { page = 1, perPage = 20, locationSlug, ...queryOptions } = options;

  return useQuery({
    queryKey: queryKeys.news.popular(locationSlug, perPage),
    queryFn: async () => {
      try {
        const response = await NewsService.fetchPopularNews(page, perPage, locationSlug);
        return response.data || [];
      } catch (error) {
        console.error('Popular news fetch error:', error);
        return [];
      }
    },
    ...defaultNewsOptions,
    ...queryOptions,
  });
};

/**
 * Hook for fetching hot news
 */
export const useHotNews = (options: UseNewsListOptions = {}) => {
  const { page = 1, perPage = 20, locationSlug, ...queryOptions } = options;

  return useQuery({
    queryKey: queryKeys.news.hot(locationSlug, perPage),
    queryFn: async () => {
      try {
        const response = await NewsService.fetchHotNews(page, perPage, locationSlug);
        return response.data || [];
      } catch (error) {
        console.error('Hot news fetch error:', error);
        // Fallback to latest news
        try {
          const fallbackResponse = await NewsService.fetchLatestNews(page, Math.min(perPage, 10), locationSlug);
          return fallbackResponse.data || [];
        } catch (fallbackError) {
          console.error('Hot news fallback error:', fallbackError);
          return [];
        }
      }
    },
    ...defaultNewsOptions,
    ...queryOptions,
  });
};

/**
 * Hook for fetching viral news
 */
export const useViralNews = (options: UseNewsListOptions = {}) => {
  const { page = 1, perPage = 20, locationSlug, ...queryOptions } = options;

  return useQuery({
    queryKey: queryKeys.news.viral(locationSlug, perPage),
    queryFn: async () => {
      try {
        const response = await NewsService.fetchViralNews(page, perPage, locationSlug);
        return response.data || [];
      } catch (error) {
        console.error('Viral news fetch error:', error);
        // Fallback to trending news
        try {
          const fallbackResponse = await NewsService.fetchTrendingNews(page, Math.min(perPage, 10), locationSlug);
          return fallbackResponse.data || [];
        } catch (fallbackError) {
          console.error('Viral news fallback error:', fallbackError);
          return [];
        }
      }
    },
    ...defaultNewsOptions,
    ...queryOptions,
  });
};

/**
 * Hook for fetching celebrity news
 */
export const useCelebrityNews = (options: UseNewsListOptions = {}) => {
  const { page = 1, perPage = 20, locationSlug, ...queryOptions } = options;

  return useQuery({
    queryKey: queryKeys.news.celebrity(locationSlug, perPage),
    queryFn: async () => {
      try {
        const response = await NewsService.fetchCelebrityNews(page, perPage, locationSlug);
        return response.data || [];
      } catch (error) {
        console.error('Celebrity news fetch error:', error);
        // Fallback to entertainment category or popular news
        try {
          const fallbackResponse = await NewsService.fetchNewsByCategory('hiburan', page, Math.min(perPage, 10), locationSlug);
          if (fallbackResponse.data && fallbackResponse.data.length > 0) {
            return fallbackResponse.data;
          }
          // Secondary fallback to popular news
          const popularResponse = await NewsService.fetchPopularNews(page, Math.min(perPage, 10), locationSlug);
          return popularResponse.data || [];
        } catch (fallbackError) {
          console.error('Celebrity news fallback error:', fallbackError);
          return [];
        }
      }
    },
    ...defaultNewsOptions,
    ...queryOptions,
  });
};

/**
 * Hook for fetching most-seen news
 */
export const useMostSeeNews = (options: UseNewsListOptions = {}) => {
  const { page = 1, perPage = 20, locationSlug, ...queryOptions } = options;

  return useQuery({
    queryKey: queryKeys.news.mostSee(locationSlug, perPage),
    queryFn: async () => {
      try {
        const response = await NewsService.fetchMostSeeNews(page, perPage, locationSlug);
        return response.data || [];
      } catch (error) {
        console.error('Most seen news fetch error:', error);
        return [];
      }
    },
    ...defaultNewsOptions,
    ...queryOptions,
  });
};

/**
 * Hook for fetching news by category
 */
export const useNewsByCategory = (options: UseNewsCategoryOptions) => {
  const { categorySlug, page = 1, perPage = 20, locationSlug, sortBy = 'latest', ...queryOptions } = options;

  return useQuery({
    queryKey: queryKeys.news.byCategory(categorySlug, page, locationSlug),
    queryFn: async () => {
      try {
        const response = await NewsService.fetchNewsByCategory(categorySlug, page, perPage, locationSlug, sortBy);
        return response.data || [];
      } catch (error) {
        console.error(`Category news fetch error (${categorySlug}):`, error);
        return [];
      }
    },
    enabled: !!categorySlug,
    ...defaultNewsOptions,
    ...queryOptions,
  });
};

/**
 * Hook for fetching news by tag
 */
export const useNewsByTag = (tagSlug: string, options: UseNewsListOptions = {}) => {
  const { page = 1, perPage = 20, locationSlug, sortBy = 'latest', ...queryOptions } = options;

  return useQuery({
    queryKey: queryKeys.news.byTag(tagSlug, page, locationSlug),
    queryFn: async () => {
      try {
        const response = await NewsService.fetchNewsByTag(tagSlug, page, perPage, locationSlug, sortBy);
        return response.data || [];
      } catch (error) {
        console.error(`Tag news fetch error (${tagSlug}):`, error);
        return [];
      }
    },
    enabled: !!tagSlug,
    ...defaultNewsOptions,
    ...queryOptions,
  });
};

/**
 * Hook for searching news
 */
export const useSearchNews = (options: UseNewsSearchOptions) => {
  const { searchTerm, page = 1, perPage = 20, locationSlug, sortBy = 'latest', ...queryOptions } = options;

  return useQuery({
    queryKey: queryKeys.news.search(searchTerm, page, locationSlug),
    queryFn: async () => {
      try {
        const response = await NewsService.searchNews(searchTerm, page, perPage, locationSlug, sortBy);
        return response.data || [];
      } catch (error) {
        console.error(`Search news error (${searchTerm}):`, error);
        return [];
      }
    },
    enabled: !!searchTerm && searchTerm.length >= 2,
    staleTime: STALE_TIME.SEARCH,
    gcTime: GC_TIME.SEARCH,
    ...defaultNewsOptions,
    ...queryOptions,
  });
};

/**
 * Hook for fetching single news item by slug
 */
export const useNewsDetail = (slug: string, options: UseNewsOptions = {}) => {
  return useQuery({
    queryKey: queryKeys.news.detail(slug),
    queryFn: async () => {
      try {
        return await NewsService.getNewsBySlug(slug);
      } catch (error) {
        console.error(`News detail fetch error (${slug}):`, error);
        throw error; // Re-throw for detail pages as we want to show error state
      }
    },
    enabled: !!slug,
    ...defaultNewsOptions,
    ...options,
  });
};

/**
 * Hook for infinite scrolling news lists
 */
export const useInfiniteNews = (
  type: 'latest' | 'popular' | 'hot' | 'viral' | 'celebrity' | 'mostSee',
  options: UseNewsListOptions = {}
) => {
  const { perPage = 20, locationSlug, ...queryOptions } = options;

  return useInfiniteQuery({
    queryKey: [...queryKeys.news.lists(), type, 'infinite', { locationSlug, perPage }],
    queryFn: async ({ pageParam = 1 }) => {
      try {
        let response;
        switch (type) {
          case 'latest':
            response = await NewsService.fetchLatestNews(pageParam, perPage, locationSlug);
            break;
          case 'popular':
            response = await NewsService.fetchPopularNews(pageParam, perPage, locationSlug);
            break;
          case 'hot':
            response = await NewsService.fetchHotNews(pageParam, perPage, locationSlug);
            break;
          case 'viral':
            response = await NewsService.fetchViralNews(pageParam, perPage, locationSlug);
            break;
          case 'celebrity':
            response = await NewsService.fetchCelebrityNews(pageParam, perPage, locationSlug);
            break;
          case 'mostSee':
            response = await NewsService.fetchMostSeeNews(pageParam, perPage, locationSlug);
            break;
          default:
            response = await NewsService.fetchLatestNews(pageParam, perPage, locationSlug);
        }
        return response;
      } catch (error) {
        console.error(`Infinite ${type} news fetch error:`, error);
        return {
          status: false,
          message: 'Failed to fetch news',
          data: [],
          meta: {
            total: 0,
            per_page: perPage,
            current_page: pageParam,
            last_page: 1,
          },
        };
      }
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage?.meta) return undefined;
      const { current_page, last_page } = lastPage.meta;
      return current_page < last_page ? current_page + 1 : undefined;
    },
    initialPageParam: 1,
    ...defaultNewsOptions,
    ...queryOptions,
  });
};

/**
 * Hook for infinite scrolling category news
 */
export const useInfiniteNewsByCategory = (categorySlug: string, options: UseNewsListOptions = {}) => {
  const { perPage = 20, locationSlug, sortBy = 'latest', ...queryOptions } = options;

  return useInfiniteQuery({
    queryKey: [...queryKeys.news.byCategory(categorySlug, 1, locationSlug), 'infinite'],
    queryFn: async ({ pageParam = 1 }) => {
      try {
        return await NewsService.fetchNewsByCategory(categorySlug, pageParam, perPage, locationSlug, sortBy);
      } catch (error) {
        console.error(`Infinite category news fetch error (${categorySlug}):`, error);
        return {
          status: false,
          message: 'Failed to fetch category news',
          data: [],
          meta: {
            total: 0,
            per_page: perPage,
            current_page: pageParam,
            last_page: 1,
          },
        };
      }
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage?.meta) return undefined;
      const { current_page, last_page } = lastPage.meta;
      return current_page < last_page ? current_page + 1 : undefined;
    },
    initialPageParam: 1,
    enabled: !!categorySlug,
    ...defaultNewsOptions,
    ...queryOptions,
  });
};

/**
 * Utility hook for prefetching news
 */
export const usePrefetchNews = () => {
  const { prefetchNews } = require('@/lib/queryClient').cacheUtils;

  return {
    prefetchLatest: (locationSlug?: string, perPage: number = 20) =>
      prefetchNews('latest', { locationSlug, perPage }),
    prefetchPopular: (locationSlug?: string, perPage: number = 20) =>
      prefetchNews('popular', { locationSlug, perPage }),
    prefetchHot: (locationSlug?: string, perPage: number = 20) =>
      prefetchNews('hot', { locationSlug, perPage }),
    prefetchViral: (locationSlug?: string, perPage: number = 20) =>
      prefetchNews('viral', { locationSlug, perPage }),
    prefetchCelebrity: (locationSlug?: string, perPage: number = 20) =>
      prefetchNews('celebrity', { locationSlug, perPage }),
  };
};

/**
 * Hook for managing news cache
 */
export const useNewsCache = () => {
  const { invalidateNews, invalidateNewsType, getCachedNews, getCacheStats } = require('@/lib/queryClient').cacheUtils;

  return {
    invalidateAll: invalidateNews,
    invalidateType: invalidateNewsType,
    getCached: getCachedNews,
    getStats: getCacheStats,
  };
};

// Export all hooks
export default {
  useLatestNews,
  usePopularNews,
  useHotNews,
  useViralNews,
  useCelebrityNews,
  useMostSeeNews,
  useNewsByCategory,
  useNewsByTag,
  useSearchNews,
  useNewsDetail,
  useInfiniteNews,
  useInfiniteNewsByCategory,
  usePrefetchNews,
  useNewsCache,
};
